import {
  Button,
  OutlinedSelect,
  Select,
  SelectOptionType,
  TextOutlinedInput,
} from '@octano/global-ui';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Container, Form, Row } from 'reactstrap';
import { TranslationsKeys } from '../../../locales/translations';
import { useIsQueryEnabled } from '../hooks/useIsQueryEnabled';
import { useWarningsSummaryDataLoader } from '../loaders/StudentWarningsSummaryDataLoader';
import { useWarningsSummaryFormLoaderData } from '../loaders/StudentWarningsSummaryFormLoader';
import { StudentWarningsRiskLevel } from '../types';

type StudentWarningsSummaryFiltersParams = {
  riskLevel?: SelectOptionType;
  studyPlan?: SelectOptionType;
  campus?: SelectOptionType;
  name?: string;
};

const StudentWarningsSummaryFilters = () => {
  const { t } = useTranslation(TranslationsKeys.STUDENT_ALERTS_SUMMARY);
  const data = useWarningsSummaryFormLoaderData();
  const { loading, query, updateQuery } = useWarningsSummaryDataLoader();
  const {
    handleSubmit,
    reset,
    control,
  } = useForm<StudentWarningsSummaryFiltersParams>();

  const isQueryEnabled = useIsQueryEnabled(query);

  const riskLevelsOptions: SelectOptionType[] = useMemo(
    () => [
      {
        label: t('riskLevels.low'),
        value: StudentWarningsRiskLevel.Low,
      },
      {
        label: t('riskLevels.medium'),
        value: StudentWarningsRiskLevel.Medium,
      },
      {
        label: t('riskLevels.high'),
        value: StudentWarningsRiskLevel.High,
      },
    ],
    [t],
  );

  const handleSearch = useCallback(
    (filters: StudentWarningsSummaryFiltersParams) => {
      updateQuery({
        ...query,
        page: 1,
        riskLevel: filters.riskLevel?.value as string,
        studyPlanId: filters.studyPlan?.value as string,
        campusId: filters.campus?.value as string,
        name: filters?.name?.trim() || undefined,
      });
    },
    [updateQuery, query],
  );

  const handleClear = useCallback(() => {
    updateQuery({
      ...query,
      page: 1,
      riskLevel: undefined,
      studyPlanId: undefined,
      campusId: undefined,
      name: undefined,
    });
    reset({
      studyPlan: undefined,
      campus: undefined,
      riskLevel: undefined,
      name: undefined,
    });
  }, [updateQuery, query, reset]);

  const handleChangeSchool = useCallback(
    (option: SelectOptionType) => {
      updateQuery({ ...query, schoolId: option.value as string, page: 1 });
    },
    [updateQuery, query],
  );

  const handleChangeYear = useCallback(
    (option: SelectOptionType) => {
      updateQuery({ ...query, year: +option.value, page: 1 });
    },
    [updateQuery, query],
  );

  const handleChangePeriod = useCallback(
    (option: SelectOptionType) => {
      updateQuery({ ...query, periodId: option.value as string, page: 1 });
    },
    [updateQuery, query],
  );

  return (
    <Container fluid>
      <Form onSubmit={handleSubmit(handleSearch)}>
        <Row className="pb-4 gutters-filters">
          <Col xs={12}>
            <p>{t('filters.prefix')}</p>
          </Col>
          <Col xs={12} md={4}>
            <Select
              name="school"
              options={data.schools}
              label={t('filters.school.label')}
              placeholder={t('filters.school.placeholder')}
              onChange={handleChangeSchool}
              isClearable={false}
              disabled={loading || data.schools.length < 1}
            />
          </Col>
          <Col xs={12} md={4}>
            <Select
              name="year"
              options={data.years}
              label={t('filters.year.label')}
              placeholder={t('filters.year.placeholder')}
              onChange={handleChangeYear}
              isClearable={false}
              disabled={loading || data.years.length < 1}
            />
          </Col>
          <Col xs={12} md={4}>
            <Select
              name="period"
              options={data.periods}
              label={t('filters.period.label')}
              placeholder={t('filters.period.placeholder')}
              onChange={handleChangePeriod}
              isClearable={false}
              disabled={loading || data.periods.length < 1}
            />
          </Col>
        </Row>
        <Row className="pb-4 gutters-filters">
          <Col xs={12} md={3}>
            <OutlinedSelect
              name="riskLevel"
              options={riskLevelsOptions}
              label={t('filters.riskLevel.label')}
              placeholder={t('filters.riskLevel.placeholder')}
              control={control}
              isClearable={true}
              disabled={
                !isQueryEnabled || loading || riskLevelsOptions.length < 1
              }
            />
          </Col>
          <Col xs={12} md={3}>
            <TextOutlinedInput
              name="name"
              label={t('filters.name.label')}
              placeholder={t('filters.name.placeholder')}
              control={control}
              disabled={!isQueryEnabled || loading}
            />
          </Col>
          <Col xs={12} md={3}>
            <OutlinedSelect
              name="studyPlan"
              options={data.studyPlans}
              label={t('filters.studyPlan.label')}
              placeholder={t('filters.studyPlan.placeholder')}
              control={control}
              isClearable={true}
              disabled={
                !isQueryEnabled || loading || data.studyPlans.length < 1
              }
            />
          </Col>
          <Col xs={12} md={3}>
            <OutlinedSelect
              name="campus"
              options={data.campuses}
              label={t('filters.campus.label')}
              placeholder={t('filters.campus.placeholder')}
              control={control}
              isClearable={true}
              disabled={!isQueryEnabled || loading || data.campuses.length < 1}
            />
          </Col>
          <Col xs={12} md={3}>
            <OutlinedSelect
              name="promo"
              options={[]}
              label={t('filters.promo.label')}
              placeholder={t('filters.promo.placeholder')}
              isClearable={true}
              disabled={true}
            />
          </Col>
          <Col xs={12} md={3}>
            <OutlinedSelect
              name="course"
              options={[]}
              label={t('filters.course.label')}
              placeholder={t('filters.course.placeholder')}
              isClearable={true}
              disabled={true}
            />
          </Col>
          <Col xs={12} md={6}>
            <div className="d-flex justify-content-end">
              <Button
                text={t('common:actions.search')}
                size="md"
                type="submit"
                disabled={!isQueryEnabled || loading}
              />
              <Button
                text={t('common:actions.clean')}
                className="ml-2"
                size="md"
                outlined
                type="button"
                disabled={!isQueryEnabled || loading}
                onClick={handleClear}
              />
            </div>
          </Col>
        </Row>
      </Form>
    </Container>
  );
};

export default StudentWarningsSummaryFilters;
